import type { OptimizationRun } from '../types/optimization'
import type { OptimizationOutputSummary } from '../types/optimizationOutput'

const placementColor = (index: number) => ['#2563eb', '#7c3aed', '#0891b2', '#0f766e', '#c2410c', '#be123c'][index % 6]

/**
 * Rendering-only adapter. The 3D scene receives the same canonical mm/kg
 * contract as PDF/CSV/XLSX, rather than reinterpreting a legacy run snapshot.
 */
export function outputSummaryViewerRun(summary: OptimizationOutputSummary): OptimizationRun | null {
  const space = summary.load_space
  if (!space?.length_mm || !space.width_mm || !space.height_mm || !Array.isArray(summary.placements)) return null

  const snapshot = summary.equipment?.scene_snapshot
  const palletGroups = summary.placements.flatMap(placement => {
    if (!placement.pallet) return []
    return [{
      pallet_id: placement.item_id,
      pallet_type: placement.pallet.type ?? 'CUSTOM',
      pallet_weight_kg: placement.weight_kg ?? 0,
      pallet_dims: {
        length: placement.pallet.dimensions_mm.length ?? placement.dimensions_mm.length ?? 0,
        width: placement.pallet.dimensions_mm.width ?? placement.dimensions_mm.width ?? 0,
        height: placement.pallet.dimensions_mm.height ?? placement.dimensions_mm.height ?? 0,
      },
      pallet_board_height_mm: placement.pallet.board_height_mm ?? undefined,
      pallet_color: placement.pallet.color ?? placement.color ?? undefined,
      children_positions: placement.pallet.children.map(child => ({
        item_id: child.item_id,
        source_id: child.source_item_id ?? undefined,
        description: child.description ?? undefined,
        color: child.color ?? undefined,
        length: child.length,
        width: child.width,
        height: child.height,
        weight: child.weight_kg ?? 0,
        local_x: child.local_x,
        local_y: child.local_y,
        local_z: child.local_z,
      })),
    }]
  })
  return {
    id: summary.identity.run_id ?? 'optimization-plan',
    plan_version: summary.identity.plan_version ?? undefined,
    status: 'completed',
    requestedAt: '',
    createdBy: 'output-summary-v1',
    input: {
      loadSpace: {
        length: space.length_mm,
        width: space.width_mm,
        height: space.height_mm,
        maxWeight: space.max_payload_kg ?? undefined,
      },
      vehicle: (snapshot?.vehicle ?? undefined) as any,
      tractor: (snapshot?.tractor ?? undefined) as any,
      vehicleCombination: snapshot?.coupling ? {
        type: 'tractor_semitrailer', trailerVehicleId: 'summary-trailer', tractorVehicleId: 'summary-tractor',
        coupling: {
          trailerKingPinPosition: snapshot.coupling.trailer_king_pin_position_mm ?? undefined,
          tractorFifthWheelPosition: snapshot.coupling.tractor_fifth_wheel_position_mm ?? undefined,
          fifthWheelMaxLoad: snapshot.coupling.fifth_wheel_max_load_kg ?? undefined,
        }
      } as any : undefined,
      items: summary.placements.map((placement, index) => ({
        id: placement.item_id,
        description: summary.cargo.articles.find(article => article.id === placement.source_item_id)?.description ?? undefined,
        length: placement.dimensions_mm.length ?? 0,
        width: placement.dimensions_mm.width ?? 0,
        height: placement.dimensions_mm.height ?? 0,
        weight: placement.weight_kg ?? 0,
        quantity: 1,
        color: placement.color ?? summary.cargo.articles.find(article => article.id === placement.source_item_id)?.color ?? placementColor(index),
        stackable: false,
        rotatable: false,
      })) as any,
    },
    result: {
      placements: summary.placements.map(placement => ({
        item_id: placement.item_id,
        itemId: placement.item_id,
        metadata: { source_id: placement.source_item_id },
        loading_order: placement.loading_order ?? undefined,
        x: placement.position_mm.x ?? 0,
        y: placement.position_mm.y ?? 0,
        z: placement.position_mm.z ?? 0,
        length: placement.dimensions_mm.length ?? 0,
        width: placement.dimensions_mm.width ?? 0,
        height: placement.dimensions_mm.height ?? 0,
        weight: placement.weight_kg ?? 0,
      })),
      pallet_groups: palletGroups,
      violations: (summary.alerts?.violations ?? []).map(alert => ({
        type: alert.type, severity: alert.severity, message: alert.message, item_ids: alert.item_ids,
      })),
    } as any,
  }
}
